import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { AsyncStorage } from '@utils/storage';

// TA-10152: the mobile composer's typed-but-unsent text lives in MyChatForm's
// local `body` state and, mirrored, in the Apollo cache draft — but the cache
// is only flushed to disk on the AppState 'background' trigger, so a kill
// that skips that event (crash, force stop, OS reclaim) loses the draft
// entirely. This hook writes the draft text to AsyncStorage on every change,
// keyed by chatId, and restores it on the next mount of that chat's composer.
// The write is deliberately NOT debounced: native storage is MMKV (a
// synchronous memory-mapped set, sub-millisecond), and any deferral window is
// exactly where a force-kill lands — QA killed the app inside the previous
// 400ms debounce window and lost the trailing keystrokes.
// Mobile only — the web composer already round-trips drafts through the
// persisted Apollo cache via updateDraftMessage/getDraftMessage.
export const DRAFT_TEXT_STORAGE_PREFIX = 'chat_draft_text_';

// Read Platform.OS lazily (not at module scope) so the check reflects the
// platform at call time — module-scope evaluation freezes whatever OS the
// test harness had at import and can't be overridden per test.
const shouldPersistDraftText = () => Platform.OS !== 'web';

export const getDraftTextStorageKey = (chatId: string) =>
  `${DRAFT_TEXT_STORAGE_PREFIX}${chatId}`;

type UseDraftTextPersistenceArgs = {
  chatId?: string;
  /** false disables restore and writes (e.g. the bottom-sheet composer). */
  enabled: boolean;
  /**
   * Called with the persisted draft when one survived a previous process.
   * Guard against clobbering text the user already typed, e.g.
   * `setBody((prev) => prev || saved)`.
   */
  onRestore: (text: string) => void;
};

export const useDraftTextPersistence = ({
  chatId,
  enabled,
  onRestore,
}: UseDraftTextPersistenceArgs) => {
  // Consumers must not write the draft (to cache or storage) until hydration
  // has resolved — an effect that fires on first mount with an empty body
  // would otherwise wipe the surviving draft before it can be restored.
  // Hydration is tracked per chatId (not as a boolean) so that if the same
  // component instance switches chats, `draftTextHydrated` flips back to
  // false in the very same render — a boolean reset inside the effect would
  // leave one render window where writes to the new chat's key were allowed
  // before its storage read resolved.
  const [hydratedChatId, setHydratedChatId] = useState<string | undefined>(
    undefined
  );
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const persistenceInactive = !enabled || !chatId || !shouldPersistDraftText();
  const draftTextHydrated = persistenceInactive || hydratedChatId === chatId;

  useEffect(() => {
    if (persistenceInactive || !chatId) return;
    let cancelled = false;
    (async () => {
      try {
        const saved = await AsyncStorage.getItem(
          getDraftTextStorageKey(chatId)
        );
        if (!cancelled && saved) {
          onRestoreRef.current(saved);
        }
      } catch {
        // storage read errors — skip the restore, same as no draft existing
      } finally {
        if (!cancelled) {
          setHydratedChatId(chatId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatId, enabled]);

  // Immediate write on every change — the storage copy must always hold the
  // latest keystroke so a force-kill at any instant preserves the full draft.
  // Identity is keyed on chatId/enabled (like the writer it replaces) so
  // consumer effects that depend on it re-run on chat switch.
  const persistDraftText = useCallback(
    (text: string) => {
      if (!enabled || !chatId || !shouldPersistDraftText()) return;
      if (text) {
        AsyncStorage.setItem(getDraftTextStorageKey(chatId), text).catch(() => {
          // best-effort — losing a persisted draft write is no worse
          // than the pre-fix behavior
        });
      } else {
        AsyncStorage.deleteItem(getDraftTextStorageKey(chatId)).catch(() => {
          // best-effort
        });
      }
    },
    [chatId, enabled]
  );

  // Immediate delete for the send path — runs synchronously in the send
  // handler, before the next render's draft-write effect, so a kill right
  // after send cannot resurrect a draft that was already sent.
  const clearPersistedDraftText = () => {
    if (!enabled || !chatId || !shouldPersistDraftText()) return;
    AsyncStorage.deleteItem(getDraftTextStorageKey(chatId)).catch(() => {
      // best-effort
    });
  };

  return { draftTextHydrated, persistDraftText, clearPersistedDraftText };
};
